import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { baseUrl } from "../../Environments/environment";

export default function PublicPassDownloader() {
	const { qrId } = useParams();
	const [qrDetails, setQrDetails] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [isWalletDownloading, setIsWalletDownloading] = useState(false);

	useEffect(() => {
		const fetchDetails = async () => {
			try {
				const response = await axios.get(`${baseUrl}/api/qrs/${qrId}/details`);
				if (response.data && response.data.result) {
					setQrDetails(response.data.result);
				} else {
					setError("Failed to load card details");
				}
			} catch (err) {
				console.error("Error fetching pass details:", err);
				setError("Invalid or expired sharing link");
			} finally {
				setLoading(false);
			}
		};

		fetchDetails();
	}, [qrId]);

	const handleDownload = () => {
		if (isWalletDownloading) return;
		setIsWalletDownloading(true);

		// Direct redirection for iOS native integration
		setTimeout(() => {
			window.location.href = `${baseUrl}/api/qrs/${qrId}/wallet`;
		}, 100);

		setTimeout(() => {
			setIsWalletDownloading(false);
		}, 2500);
	};

	if (loading) {
		return (
			<div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 text-gray-900">
				<Loader2 className="h-10 w-10 animate-spin text-indigo-600 mb-4" />
				<p className="text-sm font-semibold tracking-wide text-gray-500">Loading pass details...</p>
			</div>
		);
	}

	if (error || !qrDetails) {
		return (
			<div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 text-gray-900 p-6">
				<div className="w-full max-w-md bg-white rounded-[2rem] p-8 border border-gray-100 shadow-xl text-center">
					<div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-6 text-red-500 ring-4 ring-red-50">
						<svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
						</svg>
					</div>
					<h3 className="text-xl font-bold mb-2 tracking-tight">Oops! Something went wrong</h3>
					<p className="text-sm text-gray-500 leading-relaxed mb-6">{error || "This card does not exist or has been deleted."}</p>
				</div>
			</div>
		);
	}

	const employeeName = qrDetails.userId?.name ||
		(qrDetails.userId?.firstName ? `${qrDetails.userId.firstName} ${qrDetails.userId.lastName || ""}`.trim() : "");

	return (
		<div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 text-gray-900 p-4 sm:p-6">
			<div className="w-full max-w-md bg-white rounded-[2rem] p-5 sm:p-8 border border-gray-100 shadow-xl flex flex-col items-center">

				<h2 className="text-2xl font-semibold text-center text-gray-900 mb-6 tracking-tight">
					Add Your Review Card
				</h2>

				{/* Card Preview Mockup */}
				<div className="w-full bg-gradient-to-br from-[#2a1190] to-[#190956] text-white rounded-3xl p-5 sm:p-6 shadow-xl shadow-purple-900/10 border border-[#3b20bb]/20 mb-8 flex flex-col justify-between max-w-[320px]">
					<div className="flex justify-between items-center gap-3">
						<div className="flex-1 min-w-0">
							<img src="/logo.svg" alt="Logo" className="h-7 w-auto object-contain" />
						</div>
						<div className="flex-shrink-0 flex gap-1.5">
							<div className="bg-white/10 text-white/90 border border-white/20 px-2 py-0.5 rounded-full text-[8px] font-semibold uppercase tracking-wider">
								PVC
							</div>
							<div className="bg-white/20 text-white px-2.5 py-0.5 rounded-full text-[8px] font-semibold uppercase tracking-wider">
								Pass
							</div>
						</div>
					</div>

					{/* Strip Image */}
					<div className="my-3 w-full overflow-hidden rounded-xl border border-white/10 shadow-sm">
						<img src="/Wallet_asset.png" alt="Card Strip" className="w-full h-auto object-cover" />
					</div>

					<div className="mt-4 flex-1 flex flex-col justify-end">
						<div className="grid grid-cols-2 gap-3 items-end">
							{employeeName && (
								<div className="min-w-0">
									<p className="text-[9px] font-semibold text-purple-200/80 uppercase tracking-wider mb-0.5">Employee</p>
									<p className="text-sm font-semibold text-white leading-tight break-words line-clamp-2">{employeeName}</p>
								</div>
							)}
							<div className="min-w-0">
								<p className="text-[9px] font-semibold text-purple-200/80 uppercase tracking-wider mb-0.5">Property</p>
								<p className="text-sm font-semibold text-white leading-tight break-words line-clamp-2">{qrDetails.propertyName || "OneLyk Member"}</p>
							</div>
						</div>
					</div>
				</div>

				{/* Action Area */}
				<div className="w-full flex flex-col items-center gap-4">
					<p className="text-center text-xs text-gray-500 max-w-[280px] leading-relaxed">
						Tap below to add this card to your iPhone's Apple Wallet app for quick offline scans.
					</p>

					<button
						onClick={handleDownload}
						disabled={isWalletDownloading}
						className="transition-transform active:scale-[0.98] relative flex-shrink-0 w-[180px] h-[55px] mt-2 shadow-md shadow-gray-200"
					>
						<img
							src="/Add_to_Apple_Wallet_badge.svg.png"
							alt="Add to Apple Wallet"
							className={`w-full h-full object-contain rounded-xl hover:scale-[1.02] transition-transform cursor-pointer ${isWalletDownloading ? "opacity-50 pointer-events-none" : ""}`}
						/>
						{isWalletDownloading && (
							<div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-xl">
								<Loader2 className="h-5 w-5 animate-spin text-white" />
							</div>
						)}
					</button>
				</div>
			</div>
		</div>
	);
}
